/* Chug components (ours) — replaces Shrine premium elements whose JS lives only in
 * the license-gated main.js this fork cannot load. Defines: internal-video,
 * sticky-atc, dynamic-dates, promo-popup, predictive-search, search-form,
 * quantity-breaks (PDP price sync only — selection itself is native radio + CSS).
 * Every definition is guarded; failures in one component never break the others.
 */
(function () {
  'use strict';

  function define(tag, cls) {
    try {
      if (!customElements.get(tag)) customElements.define(tag, cls);
    } catch (e) {
      console.error('chug-components: failed to define', tag, e);
    }
  }

  function formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    const noDecimals = Math.round(cents / 100).toString();
    const withComma = amount.replace('.', ',');
    let f = format || '{{amount}}';
    return f
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  /* ---------------- internal-video ---------------- */
  define('internal-video', class extends HTMLElement {
    connectedCallback() {
      // init here, not in the constructor: Splide clones slides and a clone's
      // constructor runs before its children are attached.
      if (this._init) return;
      this._init = true;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.playBtn = this.querySelector('.internal-video__play');
      this.soundBtn = this.querySelector('.internal-video__sound-btn');
      this.timeline = this.querySelector('.internal-video__timeline');
      this.autoplay = this.dataset.autoplay === 'true';
      this.loaded = !this.querySelector('source[data-src]');

      if (this.playBtn) this.playBtn.addEventListener('click', () => this.togglePlay());
      this.video.addEventListener('click', () => this.togglePlay());
      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.video.muted = !this.video.muted;
          this.classList.toggle('internal-video--muted', this.video.muted);
        });
      }
      this.video.addEventListener('play', () => this.classList.add('internal-video--playing'));
      this.video.addEventListener('pause', () => this.classList.remove('internal-video--playing'));
      if (this.timeline) {
        this.video.addEventListener('timeupdate', () => {
          if (this.video.duration) {
            this.timeline.style.setProperty('--completed', (this.video.currentTime / this.video.duration) * 100 + '%');
          }
        });
      }
      // Muted-autoplay videos lazy-load via data-src and play only while on screen.
      if (this.autoplay) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              // Wait for window load so autoplay video never competes with the LCP image.
              if (document.readyState !== 'complete') {
                window.addEventListener('load', () => this.inView && this.startAutoplay(), { once: true });
                return;
              }
              this.startAutoplay();
            } else {
              this.video.pause();
            }
          });
        }, { threshold: 0.3 }).observe(this);
      }
    }

    startAutoplay() {
      this.ensureLoaded();
      this.video.muted = true;
      this.classList.add('internal-video--muted');
      this.video.play().catch(() => {});
    }

    ensureLoaded() {
      if (this.loaded) return;
      this.loaded = true;
      this.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
      });
      this.video.load();
    }

    togglePlay() {
      this.ensureLoaded();
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }
  });

  /* ---------------- sticky-atc ---------------- */
  define('sticky-atc', class extends HTMLElement {
    constructor() {
      super();
      // ATC button submits the main product form natively via its form="" attribute.
      if (this.dataset.scrollBtn === 'true') {
        const btn = this.querySelector('.sticky-atc__add_button, button');
        if (btn) {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            // A5 (audit 2026-10-08): Shrine stores the destination as a selector template such
            // as "#variant-selects-id" / "#ProductInfo-id" where "-id" stands for the section id.
            // getElementById("#…-id") never matched, so every sticky bar scrolled to the top.
            let dest = this.dataset.scrollDestination || 'top';
            let target = null;
            if (dest !== 'top') {
              dest = dest.replace(/-id$/, '-' + (this.dataset.section || ''));
              const sel = (dest.charAt(0) === '#' || dest.charAt(0) === '.') ? dest : '#' + dest;
              try { target = document.querySelector(sel); } catch (err) { target = null; }
              if (!target) target = document.getElementById(dest.replace(/^#/, ''));
              // The editor default "#variant-selects-id" cannot exist on single-variant products
              // (the picker is not rendered), so fall back to the buy box rather than the page top.
              if (!target) {
                const sec = this.dataset.section || '';
                const section = document.getElementById('shopify-section-' + sec) || document;
                target = document.getElementById('ProductInfo-' + sec)
                      || section.querySelector('quantity-breaks, product-form[data-main="true"], .product__info-wrapper')
                      || document.getElementById('MainProduct-' + sec);
              }
            }
            if (dest === 'top' || !target) window.scrollTo({ top: 0, behavior: 'smooth' });
            else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      }
      if (this.dataset.afterScroll === 'true') {
        const anchor = document.querySelector('product-form[data-main="true"]')
                    || document.querySelector('.product__info-wrapper')
                    || document.body;
        const onScroll = () => {
          const rect = anchor.getBoundingClientRect();
          const passed = rect.bottom < 0 || window.scrollY > 600;
          this.style.transform = passed ? 'translateY(0)' : '';
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }
  });

  /* ---------------- dynamic-dates (estimated shipping) ---------------- */
  define('dynamic-dates', class extends HTMLElement {
    constructor() {
      super();
      try { this.render(); } catch (e) { console.error('dynamic-dates:', e); }
    }

    render() {
      const dayLabels = (this.dataset.dayLabels || 'Mon,Tue,Wed,Thu,Fri,Sat,Sun').split(',').map((s) => s.trim());
      const monthLabels = (this.dataset.monthLabels || 'Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec').split(',').map((s) => s.trim());
      const skipDays = (this.dataset.daysToSkip || '').split(',').filter(Boolean);
      const skipDates = (this.dataset.datesToSkip || '').split(',').filter(Boolean);
      const format = this.dataset.dateFormat || 'mm_dd';
      const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

      const isSkipped = (d) => {
        if (skipDays.includes(WEEKDAYS[d.getDay()])) return true;
        const dm = d.getDate() + '/' + (d.getMonth() + 1);
        const dm0 = String(d.getDate()).padStart(2, '0') + '/' + String(d.getMonth() + 1).padStart(2, '0');
        return skipDates.includes(dm) || skipDates.includes(dm0);
      };
      const addBusinessDays = (days) => {
        const d = new Date();
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (!isSkipped(d)) added++;
        }
        return d;
      };
      const ord = (n) => {
        const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const fmt = (d) => {
        // dayLabels follow the Mon-first convention of the block default
        const day = dayLabels[(d.getDay() + 6) % 7] || '';
        const m = monthLabels[d.getMonth()] || '';
        const dd2 = String(d.getDate()).padStart(2, '0');
        const mm2 = String(d.getMonth() + 1).padStart(2, '0');
        switch (format) {
          case 'day_mm_dd': return day + ', ' + m + ' ' + ord(d.getDate());
          case 'day_dd_mm': return day + ', ' + d.getDate() + '. ' + m;
          case 'dd_mm': return d.getDate() + '. ' + m;
          case 'dd_mm_no_dot': return d.getDate() + ' ' + m;
          case 'day_dd_mm_numeric': return day + ', ' + dd2 + '. ' + mm2 + '.';
          case 'dd_mm_numeric': return dd2 + '. ' + mm2 + '.';
          default: return m + ' ' + ord(d.getDate());
        }
      };

      this.querySelectorAll('[data-dynamic-date="true"]').forEach((el) => {
        const text = el.dataset.text || '';
        if (!text) return;
        const min = parseInt(el.dataset.minDays || '0', 10);
        const max = parseInt(el.dataset.maxDays || '0', 10);
        // innerHTML, not textContent: these settings ship with markup in them — the
        // schema defaults for top_text_1/2/3 are literally '<strong>[start_date]</strong>' —
        // and snippets/shipping-checkpoints.liquid already prints the same value unescaped.
        // textContent escaped it, so every shipping timeline rendered the literal string
        // "<strong>Aug 5th</strong>" the moment this element upgraded and overwrote the
        // server-rendered markup. Same trust level as the Liquid output: a theme setting,
        // not shopper input.
        el.innerHTML = text
          .replace('[start_date]', fmt(addBusinessDays(min)))
          .replace('[end_date]', fmt(addBusinessDays(max)));
      });
    }
  });

  /* ---------------- promo-popup ---------------- */
  define('promo-popup', class extends HTMLElement {
    constructor() {
      super();
      this.modal = this.querySelector('.popup-modal');
      this.overlay = this.querySelector('.popup-overlay');
      if (!this.modal) return;
      this.storageKey = 'chug-promo-popup-shown';

      this.querySelectorAll('.promp-popup__close-btn').forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });

      const testMode = this.dataset.testMode === 'true';
      if (testMode) return; // server already rendered it active; just keep close handlers

      // Newsletter form just posted → show the popup again so the success state is visible
      const posted = window.location.search.includes('customer_posted=true');
      if (posted) { this.open(); return; }

      const delayDays = parseInt(this.dataset.delayDays || '30', 10);
      const last = parseInt(localStorage.getItem(this.storageKey) || '0', 10);
      if (last && Date.now() - last < delayDays * 864e5) return;
      const delaySec = parseInt(this.dataset.delaySeconds || '5', 10);
      setTimeout(() => this.open(), delaySec * 1000);
    }

    open() {
      this.modal.classList.add('popup-modal--active');
      if (this.overlay) this.overlay.classList.add('popup-overlay--active');
    }

    close() {
      this.modal.classList.remove('popup-modal--active');
      if (this.overlay) this.overlay.classList.remove('popup-overlay--active');
      try { localStorage.setItem(this.storageKey, String(Date.now())); } catch (e) {}
    }
  });

  /* ---------------- search-form / predictive-search ---------------- */
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButton = this.querySelector('button[type="reset"]');
      if (this.input) {
        this.input.addEventListener('input', window.debounce ? window.debounce(() => this.onChange(), 300) : () => this.onChange());
      }
      if (this.resetButton) {
        this.resetButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.input.value = '';
          this.input.focus();
          this.toggleResetButton();
          this.onChange();
        });
      }
    }
    toggleResetButton() {
      if (this.resetButton) this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
    }
    onChange() { this.toggleResetButton(); }
  }
  define('search-form', SearchForm);

  define('predictive-search', class extends SearchForm {
    constructor() {
      super();
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.cachedResults = {};
      this.abortController = new AbortController();
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.closeResults();
      });
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.closeResults();
      });
    }

    getQuery() { return this.input.value.trim(); }

    onChange() {
      super.onChange();
      const q = this.getQuery();
      if (!q.length) { this.closeResults(true); return; }
      this.getSearchResults(q);
    }

    getSearchResults(query) {
      if (this.cachedResults[query]) { this.renderSearchResults(this.cachedResults[query]); return; }
      this.setLiveRegionLoadingState();
      this.abortController.abort();
      this.abortController = new AbortController();
      const url = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      if (this.apiUnsupported) return;
      fetch(`${url}?q=${encodeURIComponent(query)}&section_id=predictive-search`, { signal: this.abortController.signal })
        .then((r) => {
          if (r.status >= 400 && r.status < 500) {
            // e.g. 417 "Unsupported buyer locale" — Shopify predictive API has no Hebrew
            // support. Degrade silently: plain form submit to /search still works.
            this.apiUnsupported = true;
            this.closeResults(true);
            throw Object.assign(new Error('predictive unsupported'), { name: 'AbortError' });
          }
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          const resultsMarkup = html ? html.innerHTML : '';
          this.cachedResults[query] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        })
        .catch((err) => { if (err.name !== 'AbortError') console.error('predictive-search:', err); });
    }

    setLiveRegionLoadingState() {
      if (this.apiUnsupported) return;
      this.setAttribute('loading', 'true');
      if (this.predictiveSearchResults) this.predictiveSearchResults.classList.add('predictive-search--loading');
    }

    renderSearchResults(markup) {
      if (!this.predictiveSearchResults) return;
      this.predictiveSearchResults.innerHTML = markup;
      this.predictiveSearchResults.classList.remove('predictive-search--loading');
      this.removeAttribute('loading');
      this.setAttribute('open', 'true');
      this.predictiveSearchResults.style.display = markup.trim() ? 'block' : 'none';
      if (this.input) this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults(clear) {
      this.removeAttribute('loading');
      if (this.predictiveSearchResults) this.predictiveSearchResults.classList.remove('predictive-search--loading');
      if (clear && this.input) this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('open');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
        this.predictiveSearchResults.style.display = '';
      }
    }
  });

  /* ---------------- quantity-breaks: sync PDP price with selected tier ---------------- */
  define('quantity-breaks', class extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.updatePdpPrices !== 'true') return;
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
      this.querySelectorAll('input[name="quantity"]').forEach((input) => {
        input.addEventListener('change', () => this.syncPdpPrice(input));
      });
    }

    syncPdpPrice(input) {
      try {
        const label = this.querySelector(`label[data-input="${input.dataset.input}"]`);
        if (!label || !this.basePrice) return;
        const qty = parseInt(label.dataset.quantity || '1', 10);
        const pctLeft = parseFloat(label.dataset.percentageLeft || '1');
        const fixed = parseFloat(label.dataset.fixedDiscount || '0');
        const price = Math.round(this.basePrice * qty * pctLeft - fixed);
        const comparePer = parseInt(label.dataset.baseComparePrice || '0', 10) || this.basePrice;
        const compare = comparePer * qty;

        const scope = this.closest('.shopify-section') || document;
        scope.querySelectorAll('.main-price').forEach((el) => { el.textContent = formatMoney(price, this.moneyFormat); });
        scope.querySelectorAll('.main-comapre-price').forEach((el) => {
          el.textContent = formatMoney(compare, this.moneyFormat);
          const wrap = el.closest('.price__compare-price');
          if (wrap) wrap.style.visibility = compare > price ? '' : 'hidden';
        });
      } catch (e) { console.error('quantity-breaks price sync:', e); }
    }
  });

  /* ---------------- quantity-gifts (gifts on quantity) ---------------- */
  define('quantity-gifts', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.gifts = Array.from(this.querySelectorAll('.quantity-gift'));
      this.scope = this.closest('.shopify-section') || document;
      const update = () => this.update();
      // main-product quantity sources: bundle tier radios + numeric steppers
      this.scope.querySelectorAll('input[name="quantity"]').forEach((el) => {
        el.addEventListener('change', update);
        el.addEventListener('input', update);
      });
      this.update();
    }

    selectedQuantity() {
      const checked = this.scope.querySelector('input[name="quantity"][type="radio"]:checked');
      if (checked) return parseInt(checked.value, 10) || 1;
      const input = this.scope.querySelector('quantity-input input[name="quantity"], .quantity__input, input[name="quantity"]');
      return input ? (parseInt(input.value, 10) || 1) : 1;
    }

    update() {
      const qty = this.selectedQuantity();
      this.gifts.forEach((g) => {
        const required = parseInt(g.dataset.quantity || '0', 10);
        const unlocked = qty >= required;
        g.dataset.unlocked = String(unlocked);
        g.classList.toggle('quantity-gift--unlocked', unlocked);
      });
    }

    unlockedVariantIds() {
      this.update();
      return this.gifts
        .filter((g) => g.dataset.unlocked === 'true' && g.dataset.product)
        .map((g) => parseInt(g.dataset.product, 10))
        .filter(Boolean);
    }
  });

  /* ---------------- comparison-slider (before/after) ---------------- */
  define('comparison-slider', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const input = this.querySelector('.comparison-slider__input');
      const overlay = this.querySelector('.comparison-slider__overlay');
      const line = this.querySelector('.comparison-slider__line');
      if (!input) return;
      const apply = () => {
        const v = input.value + '%';
        this.style.setProperty('--position', v);
        if (overlay) overlay.style.width = v;
        if (line) line.style.left = v;
      };
      input.addEventListener('input', apply);
      apply();
    }
  });

  /* ---------------- section-group (merge 2 sections side-by-side) ---------------- */
  define('section-group', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const move = (id, container) => {
        if (!id || !container) return;
        const clean = id.trim().replace(/^#/, '').replace(/^shopify-section-/, '');
        const sec = document.getElementById('shopify-section-' + clean) || document.getElementById(clean);
        if (!sec) return;
        while (sec.firstChild) container.appendChild(sec.firstChild);
        sec.style.display = 'none';
      };
      move(this.dataset.sectionOneId, this.querySelector('.section-group__section-one-container'));
      move(this.dataset.sectionTwoId, this.querySelector('.section-group__section-two-container'));
    }
  });

  /* ---------------- clickable-discount ---------------- */
  define('clickable-discount', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const btn = this.querySelector('.clickable-discount__btn') || this;
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        if (this.dataset.applied === 'true' || this.dataset.loading === 'true') return;
        const code = this.dataset.code;
        if (!code) return;
        this.dataset.loading = 'true';
        this.dataset.error = 'false';
        try {
          const r = await fetch('/discount/' + encodeURIComponent(code), { redirect: 'follow' });
          if (!r.ok && r.status !== 302) throw new Error(r.status);
          this.dataset.applied = 'true';
        } catch (err) {
          this.dataset.error = 'true';
        } finally {
          this.dataset.loading = 'false';
        }
      });
    }
  });

  /* ---------------- custom-product-field (line-item properties) ---------------- */
  define('custom-product-field', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.fieldName = this.dataset.name;
      if (!this.fieldName) return;
      const scope = this.closest('.shopify-section') || document;
      this.form = scope.querySelector('product-form[data-main="true"] form, product-form form');
      if (!this.form) return;
      this.hiddenInput = document.createElement('input');
      this.hiddenInput.type = 'hidden';
      this.hiddenInput.name = 'properties[' + this.fieldName + ']';
      this.hiddenInput.value = this.dataset.defaultValue || '';
      this.form.appendChild(this.hiddenInput);
      this.querySelectorAll('input, textarea, select').forEach((el) => {
        ['input', 'change'].forEach((ev) => el.addEventListener(ev, () => {
          if (el.type === 'radio') { if (el.checked) this.hiddenInput.value = el.value; }
          else this.hiddenInput.value = el.value;
          this.classList.remove('custom-product-field--error');
        }));
      });
    }

    validate() {
      if (this.dataset.required !== 'true') return true;
      if (this.hiddenInput && this.hiddenInput.value.trim() !== '') return true;
      this.classList.add('custom-product-field--error');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }
  });

  /* ---------------- shrine-subscription-widget ---------------- */
  define('shrine-subscription-widget', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.boxes = Array.from(this.querySelectorAll('.subscription__box'));
      const scope = this.closest('.shopify-section') || document;
      this.form = scope.querySelector('product-form[data-main="true"] form, product-form form');
      this.freqSelect = this.querySelector('select[id^="SubscriptionFrequency"]');
      this.boxes.forEach((box) => {
        box.addEventListener('click', () => this.select(box));
      });
      if (this.freqSelect) {
        this.freqSelect.addEventListener('change', () => this.syncSellingPlan());
      }
      const pre = this.boxes.find((b) => b.dataset.selected === 'true') || this.boxes[0];
      if (pre) this.select(pre);
    }

    select(box) {
      this.boxes.forEach((b) => {
        const on = b === box;
        b.dataset.selected = String(on);
        b.classList.toggle('subscription__box--selected', on);
      });
      this.syncSellingPlan();
    }

    syncSellingPlan() {
      if (!this.form) return;
      const active = this.boxes.find((b) => b.dataset.selected === 'true');
      const isSub = active && active.dataset.type === 'subscription';
      let input = this.form.querySelector('input[name="selling_plan"]');
      if (isSub) {
        const planId = (this.freqSelect && this.freqSelect.value) || this.dataset.firstSellingPlanId;
        if (!planId) return;
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          this.form.appendChild(input);
        }
        input.value = planId;
        input.disabled = false;
      } else if (input) {
        input.value = '';
        input.disabled = true;
      }
    }
  });

  /* ---------------- card-variant-selects (quick-add on product cards) ---------------- */
  define('card-variant-selects', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try {
        this.variants = JSON.parse(this.querySelector(':scope > script[type="application/json"]').textContent);
      } catch (e) { this.variants = null; }
      if (!this.variants) return;
      this.formId = this.dataset.section;
      this.addEventListener('change', () => this.onChange());
    }

    selectedOptions() {
      const groups = Array.from(this.querySelectorAll('.product-form__input'));
      return groups.map((g) => {
        const checked = g.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const sel = g.querySelector('select');
        return sel ? sel.value : null;
      });
    }

    onChange() {
      const opts = this.selectedOptions();
      const match = this.variants.find((v) => opts.every((o, i) => o === null || (v['option' + (i + 1)] || null) === o));
      const card = this.closest('.card-wrapper, .card, li, div') || document;
      const form = document.getElementById(this.formId) || card.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return;
      const idInput = form.querySelector('input[name="id"]');
      const btn = card.querySelector('.quick-add__submit');
      if (match) {
        if (idInput) { idInput.value = String(match.id); idInput.disabled = false; }
        if (btn) {
          btn.disabled = !match.available;
          const span = btn.querySelector('span');
          if (span && btn.dataset.soldOutText && !match.available) span.textContent = btn.dataset.soldOutText;
        }
      } else if (btn) {
        btn.disabled = true;
      }
    }
  });

  /* ---------------- cart-drawer-gift (gift in cart drawer, goal-gated) ---------------- */
  /* Lock state + progress text are server-rendered per cart state on every drawer
     re-render; this element only adds/removes the gift line to MATCH that state. */
  define('cart-drawer-gift', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      // wait a tick so the whole drawer re-render settles
      setTimeout(() => this.sync(), 50);
    }

    async sync() {
      const variantId = parseInt(this.dataset.id || '0', 10);
      if (!variantId) return;
      if (window.__chugGiftSyncing) return;
      window.__chugGiftSyncing = true;
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        const line = cart.items.find(i => i.variant_id === variantId);
        const unlocked = this.dataset.selected === 'true';
        if (unlocked && !line) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          this.refreshDrawer();
        } else if (!unlocked && line) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 0 })
          });
          this.refreshDrawer();
        } else if (line && line.quantity > 1) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: 1 })
          });
          this.refreshDrawer();
        }
      } catch (e) { /* cart endpoints may be bot-challenged in automation; harmless */ }
      finally { window.__chugGiftSyncing = false; }
    }

    refreshDrawer() {
      const pf = document.querySelector('product-form');
      fetch((window.routes && window.routes.cart_url || '/cart') + '?section_id=cart-drawer')
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('#CartDrawer');
          const cur = document.getElementById('CartDrawer');
          if (next && cur) cur.innerHTML = next.innerHTML;
        }).catch(() => {});
    }
  });

  /* ---------------- quantity-break-upsells (per-tier upsells/gifts) ---------------- */
  define('quantity-break-upsells', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.offer = parseInt(this.dataset.offer || '1', 10);
      this.items = Array.from(this.querySelectorAll('.quantity-break__upsell'));
      this.items.forEach((item) => {
        if (item.dataset.toggle === 'true') {
          item.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            item.dataset.selected = String(item.dataset.selected !== 'true');
          });
        }
      });
    }

    // ids to add when THIS tier is the selected one
    activeVariantIds() {
      return this.items
        .filter((i) => i.dataset.selected === 'true' && i.dataset.product)
        .map((i) => parseInt(i.dataset.product, 10))
        .filter(Boolean);
    }
  });

  /* ---------------- bundle-deals ---------------- */
  define('bundle-deals', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.moneyFormat = '{{amount}}';
      this.pctLeft = parseFloat(this.dataset.percentageLeft || '1');
      this.fixed = parseFloat(this.dataset.fixedDiscount || '0');
      this.checkboxes = Array.from(this.querySelectorAll('.bundle-deals__checkbox-js'));
      this.checkboxes.forEach((cb) => {
        cb.addEventListener('change', () => {
          cb.dataset.checked = String(cb.checked);
          this.updateTotals();
        });
      });
      // per-product variant dropdowns: resolve against each product's variants JSON
      this.querySelectorAll('.bundle-deals__product-js').forEach((prod) => {
        const jsonEl = prod.querySelector('script[type="application/json"]');
        if (!jsonEl) return;
        let variants = null;
        try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
        prod.querySelectorAll('select').forEach((sel) => {
          sel.addEventListener('change', () => {
            const opts = Array.from(prod.querySelectorAll('select')).map(s => s.value);
            const match = variants.find(v => opts.every((o, i) => (v['option' + (i + 1)] || null) === o));
            const cb = prod.querySelector('.bundle-deals__checkbox-js');
            if (match && cb) {
              cb.dataset.id = String(match.id);
              if (this.dataset.updatePrices === 'true') {
                cb.dataset.price = String(match.price);
                cb.dataset.comparePrice = String((match.compare_at_price || match.price));
              }
              this.updateTotals();
            }
          });
        });
      });
      // take over the bundle ATC (its hidden id input is intentionally empty)
      const form = this.querySelector('product-form form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.addBundle(form);
        }, true);
      }
      this.updateTotals();
    }

    selectedItems() {
      return this.checkboxes
        .filter(cb => cb.checked && cb.dataset.id)
        .map(cb => ({ id: parseInt(cb.dataset.id, 10), quantity: 1, price: parseFloat(cb.dataset.price || '0'), compare: parseFloat(cb.dataset.comparePrice || '0') }));
    }

    updateTotals() {
      if (this.dataset.updatePrices !== 'true') return;
      const items = this.selectedItems();
      const total = Math.round(items.reduce((s, i) => s + i.price, 0) * this.pctLeft - this.fixed);
      const compare = Math.round(items.reduce((s, i) => s + (i.compare || i.price), 0));
      const sym = this.dataset.currencySymbol || '';
      const fmt = (c) => sym + (c / 100).toFixed(2).replace(/\.00$/, '');
      const t = this.querySelector('.bundle-deals__total-price-js');
      const c = this.querySelector('.bundle-deals__total-compare-price-js');
      if (t) t.textContent = fmt(Math.max(total, 0));
      if (c) c.textContent = compare > total ? fmt(compare) : '';
    }

    async addBundle(form) {
      const items = this.selectedItems().map(({ id, quantity }) => ({ id, quantity }));
      if (!items.length) return;
      const btn = form.closest('product-form').querySelector('button[type="submit"]') || this.querySelector('.atc-button');
      if (btn) { btn.disabled = true; btn.classList.add('loading'); }
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!r.ok) throw new Error(r.status);
        const skip = this.querySelector('input.product-variant-id[data-skip-cart="true"]');
        if (skip) { window.location.href = '/checkout'; return; }
        const drawer = document.querySelector('cart-drawer');
        if (drawer) {
          const html = await fetch((window.routes && window.routes.cart_url || '/cart') + '?section_id=cart-drawer').then(x => x.text());
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const next = doc.querySelector('#CartDrawer');
          const cur = document.getElementById('CartDrawer');
          if (next && cur) cur.innerHTML = next.innerHTML;
          drawer.classList.remove('is-empty');
          drawer.open();
        } else {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        }
      } catch (e) {
        console.error('bundle-deals add:', e);
      } finally {
        if (btn) { btn.disabled = false; btn.classList.remove('loading'); }
      }
    }
  });
})();
